import { useEffect, useState } from "react";
import moment from "moment";
import find from "lodash/find";
import { AnyObject } from "../interfaces/AnyObject";
import { TableRecord } from "../interfaces/TableRecord";
import { CommonChildTableProps } from "../interfaces/CommonChildTableProps";
import { DictionaryDto } from "../interfaces/DictionaryDto";

const currentDate = moment();

const getIsRecordCreated = ({ isCreated }: AnyObject): boolean => isCreated;

/* Вновь созданная версия с fromDate === parentFromDate, помещается выше
  иначе сортировка по fromDate */
export const sortVersionsByDate = (recordA, recordB): number => {
  if (moment(recordA.fromDate).isSame(moment(recordB.fromDate), "day")) {
    if (getIsRecordCreated(recordA)) return -1;
    if (getIsRecordCreated(recordB)) return 1;
    return 0;
  }
  return moment(recordA.fromDate).isBefore(moment(recordB.fromDate)) ? 1 : -1;
};

export const getCommonChildTableProps = (
  version: AnyObject,
  i: number,
  versionsLength: number,
  isChild?: true
): CommonChildTableProps => ({
  key: `${version.id}_${version.fromDate}`,
  isCurrentVersion:
    moment(currentDate).isSameOrAfter(version.fromDate) &&
    moment(currentDate).isBefore(version.toDate),
  isPastVersion: moment(version?.toDate).isBefore(currentDate),
  isLatestVersion: versionsLength === i + 1,
  isChild,
});

export const geDicValueById = <T extends AnyObject>(
  dictionary: T[] | null | undefined,
  id?: number
): T | undefined => {
  const isValidDic = Array.isArray(dictionary) && dictionary[0] && "id" in dictionary[0];
  if (isValidDic && id) {
    return find(dictionary, (item) => {
      if (typeof id === "string") {
        return String(item.id) === id;
      }
      return Number(item.id) === id;
    });
  }
  return undefined;
};

export const getDictionaryItemById = <T extends { id: number }>(
  dictionary: T[] | undefined,
  id: number | undefined | null
): T | undefined => {
  if (id === undefined || id === null || dictionary === undefined) return undefined;
  return dictionary.find((item) => item.id === id);
};

// ChildColumns - пересоздание колонок используя зависимости (дизейбл в режиме редактирования и пр.)
// !Кнопки обязательно должны быть в колонке с key="actions"
export const useChildColumns = (getChildColumns: () => AnyObject[], dependencies: any[]): any => {
  const [childColumns, setChildColumns] = useState(getChildColumns());
  useEffect(() => {
    const newColumns = getChildColumns();
    const getNewColumn = (columnKey) => newColumns.find(({ key }) => key === columnKey);
    setChildColumns((prevState) =>
      prevState.map((column) => ({ ...column, render: getNewColumn(column.key)?.render }))
    );
  }, dependencies);

  return [childColumns, setChildColumns];
};

const getIsRecordCreating = ({ isCreated }: AnyObject): boolean => Boolean(isCreated);

const getIsRecordEditing = ({ isEditing }: AnyObject): boolean => Boolean(isEditing);

const getIsRecordClosing = ({ isClosing }: AnyObject): boolean => Boolean(isClosing);

export const getIsRecordChanging = ({
  isCreated,
  isEditing,
  isClosing,
  isChangingProlongation,
}: AnyObject): boolean => isCreated || isEditing || isClosing || isChangingProlongation;

// Наличие в таблице изменяемой строки
export const getIsTableChanging = (childTableData: TableRecord[]): boolean =>
  childTableData?.some(getIsRecordChanging);

// Получение изменяемой строки
export const getChangeableRecord = (childTableData: TableRecord[]): TableRecord => {
  const changeableRecord = childTableData.find(getIsRecordChanging);
  if (changeableRecord) return changeableRecord;
  throw new Error();
};

// Ширины столбцов
export const commonColumnsWidths = {
  id: "70px",
  period: "250px",
  actions: "120px",
  eye: "48px",
  buttons: "42px",
};

const versionsTableUtils = {
  sortVersionsByDate,
  getCommonChildTableProps,
  geDicValueById,
  useChildColumns,
  getIsRecordChanging,
  getIsTableChanging,
  getChangeableRecord,
  getDictionaryItemById,
  columnsWidths: commonColumnsWidths,
  getIsRecordCreating,
  getIsRecordEditing,
  getIsRecordClosing,
};

export default versionsTableUtils;
